import { useLingui } from '@lingui/react/macro';
import { Box, ToggleButton, ToggleButtonGroup, toggleButtonGroupClasses } from '@mui/material';
import { useCallback } from 'react';
import { MarginType } from '@/common/constants';
import { muiTheme } from '@/theme';

const toggleSx = {
  padding: '6px 16px',
  lineHeight: '20px',
  fontSize: '0.75rem',
};
const groupSx = {
  flex: 1,
  padding: '2px',
  boxShadow: (theme: any) => theme.vars.palette.common.shadows.perplInnerShadow,
  border: 1,
  borderColor: (theme: any) => theme.vars.palette._components.input.outlined.enabledBorder,
  background: (theme: any) => theme.vars.palette.background['paper-elevation-0'],

  [`& .${toggleButtonGroupClasses.grouped}`]: {
    borderRadius: (theme: any) => theme.vars.shape.borderRadius,
    border: 'transparent',
    color: (theme: any) => theme.vars.palette.text.primary,

    [`&.${toggleButtonGroupClasses.selected}`]: {
      boxShadow: muiTheme.vars.shadows[2],
    },
    [`&.${toggleButtonGroupClasses.selected}:hover`]: {},
  },
};

export function MarginTypeSwitch({
  value,
  onChange,
}: Readonly<{
  onChange: (value: MarginType) => void;
  value: MarginType;
}>) {
  const { t } = useLingui();
  const crossMarginIntroduced = false;
  const onSelect = useCallback(
    (_: any, value: MarginType | null) => {
      if (value == null) {
        return;
      }
      onChange(value);
    },
    [onChange]
  );

  if (!crossMarginIntroduced) {
    return null;
  }

  return (
    <>
      <Box display="flex" alignItems="center" gap={0.75}>
        <ToggleButtonGroup
          fullWidth
          exclusive
          size="small"
          value={value}
          aria-description={t({ comment: 'Margin type switch toggle', message: 'Margin type switch' })}
          sx={groupSx}
          onChange={onSelect}
        >
          <ToggleButton
            value={MarginType.isolated}
            aria-label={t({
              comment:
                'Isolated margin label. One of two labels in a narrow two-button toggle, beside "Cross" — keep both the same part of speech and roughly the same length; abbreviate if the word is long in your language (the positions table already uses a short form).',
              message: 'Isolated',
            })}
            size="small"
            sx={toggleSx}
          >
            {t({
              comment:
                'Isolated margin label. One of two labels in a narrow two-button toggle, beside "Cross" — keep both the same part of speech and roughly the same length; abbreviate if the word is long in your language (the positions table already uses a short form).',
              message: 'Isolated',
            })}
          </ToggleButton>
          <ToggleButton
            value={MarginType.cross}
            size="small"
            aria-label={t({
              comment:
                'Cross margin label. One of two labels in a narrow two-button toggle, beside "Isolated" — keep both the same part of speech and roughly the same length.',
              message: 'Cross',
            })}
            sx={toggleSx}
          >
            {t({
              comment:
                'Cross margin label. One of two labels in a narrow two-button toggle, beside "Isolated" — keep both the same part of speech and roughly the same length.',
              message: 'Cross',
            })}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </>
  );
}
